import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Link2, 
  Unlink, 
  Star, 
  Loader2, 
  Plus, 
  Facebook,
  Building2,
  Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Connections = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [platformStatus, setPlatformStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    fetchPlatformStatus();
  }, []);

  const fetchPlatformStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/platforms/status`);
      setPlatformStatus(response.data);
    } catch (error) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const startConnection = async (platform) => {
    const tier = user?.subscription_tier || 'free';
    if (tier === 'free') {
      toast.error('Subscribe to connect social platforms');
      navigate('/subscribe');
      return;
    }

    setConnecting(platform);
    try {
      const redirectUri = `${window.location.origin}/oauth/callback`;
      const response = await axios.post(
        `${API_URL}/api/platforms/connect/${platform}/start`,
        { redirect_uri: redirectUri }
      );

      if (response.data.authorization_url) {
        localStorage.setItem('oauth_return_path', '/connections');
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      if (error.response?.data?.detail === 'PRO_REQUIRED') {
        toast.error('Upgrade to Pro to connect multiple platforms');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start connection');
      }
    } finally {
      setConnecting(null);
    }
  };

  const setFavourite = async (connectionId) => {
    try {
      await axios.post(`${API_URL}/api/platforms/set-favourite`, {
        connection_id: connectionId
      });
      toast.success('Set as primary account');
      fetchPlatformStatus();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const disconnectPlatform = async (connectionId) => {
    try {
      await axios.post(`${API_URL}/api/platforms/disconnect`, {
        connection_id: connectionId
      });
      toast.success('Account disconnected');
      fetchPlatformStatus();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const tier = user?.subscription_tier || 'free';
  const isFree = tier === 'free';
  const isStandard = tier === 'standard';
  const isPro = tier === 'pro';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0a2540]" />
        </div>
      </div>
    );
  }

  const getPlatformIcon = (platform) => {
    if (platform === 'meta') return Facebook;
    if (platform === 'google') return Building2;
    return Link2;
  };

  const getPlatformName = (platform) => {
    if (platform === 'meta') return 'Facebook / Instagram';
    if (platform === 'google') return 'Google Business Profile';
    return platform;
  };

  const connections = platformStatus?.connections || [];
  const canConnectMore = isStandard ? connections.length < 1 : isPro;

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Navbar />
      
      <div className="pt-20 px-4 pb-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#0a2540]" data-testid="connections-title">
              Connected Accounts
            </h1>
            <p className="text-slate-600 mt-2">
              Connect your social accounts to post directly from SalesAssets
            </p>
          </div>

          {isFree && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-[#0a2540] font-medium">
                Subscribe to connect social accounts
              </p>
              <p className="text-slate-600 text-sm mt-1">
                Start your subscription to enable one-click posting to Facebook, Instagram, and Google Business.
              </p>
              <Button 
                onClick={() => navigate('/subscribe')}
                className="mt-3 bg-[#d4af37] text-[#0a2540] hover:bg-[#c5a028]"
                data-testid="subscribe-cta-btn"
              >
                Subscribe Now
              </Button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-6">
            <div className="p-4 border-b border-slate-100">
              <h2 className="font-bold text-[#0a2540]">Your Connections</h2>
              <p className="text-sm text-slate-500">
                {isStandard ? '1 connection included' : ''}
                {isPro ? 'Unlimited connections' : ''}
                {isFree ? 'Subscribe to connect' : ''}
              </p>
            </div>

            {connections.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {connections.map((conn) => {
                  const Icon = getPlatformIcon(conn.platform);
                  return (
                    <div key={conn.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#0a2540]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#0a2540]">{conn.account_label}</p>
                          <p className="text-sm text-slate-500">{getPlatformName(conn.platform)}</p>
                        </div>
                        {conn.is_favourite && (
                          <span className="bg-amber-100 text-[#0a2540] text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!conn.is_favourite && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFavourite(conn.id)}
                            data-testid={`set-primary-${conn.id}`}
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Set Primary
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => disconnectPlatform(conn.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`disconnect-${conn.id}`}
                        >
                          <Unlink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Link2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No accounts connected</p>
                {!isFree && (
                  <p className="text-sm text-slate-400 mt-1">
                    Connect your first account below
                  </p>
                )}
              </div>
            )}
          </div>

          {!isFree && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h2 className="font-bold text-[#0a2540]">Add Connection</h2>
              </div>

              <div className="p-4">
                {/* Meta (Facebook/Instagram) Only */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Facebook className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0a2540]">Facebook / Instagram</p>
                      <p className="text-sm text-slate-500">Post to your pages</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => startConnection('meta')}
                    disabled={connecting === 'meta' || !canConnectMore}
                    className="bg-[#0a2540]"
                    data-testid="connect-meta-btn"
                  >
                    {connecting === 'meta' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <span className="flex items-center">
                        <Plus className="w-4 h-4 mr-1" />
                        Connect
                      </span>
                    )}
                  </Button>
                </div>
              </div>

              {isStandard && connections.length >= 1 && (
                <div className="p-4 bg-[#0a2540] text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-[#d4af37]" />
                    <p className="font-medium">Need more connections?</p>
                  </div>
                  <p className="text-sm text-slate-300 mb-3">
                    Upgrade to Pro to connect unlimited social accounts
                  </p>
                  <Button
                    onClick={() => navigate('/subscribe?tier=pro')}
                    className="bg-[#d4af37] text-[#0a2540] hover:bg-[#c5a028]"
                    data-testid="upgrade-pro-btn"
                  >
                    Upgrade to Pro - £15/month
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-center text-sm text-slate-500">
            <p>
              Connected accounts allow you to post directly from SalesAssets.
            </p>
            <p>
              Your login credentials are never stored.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Connections;
